import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ShotGraph } from "./ShotGraph";
import { useFrameInterpolation } from "./frame-interpolation";
import {
  createDemoDevice,
  type CommandOutcome,
  type ConfirmationRequest,
  type DeviceState,
  type HeadId,
  type HeadState,
  type SettingDescriptor,
  type SettingGroup,
  type SnapshotSummary,
} from "./model";
import type { RemoteApi } from "./remote-api";
import { commandBlocksMutation, initialRemoteState, remoteReducer, settingsChanged, validateSettings } from "./state";
import { webBluetoothSupport } from "./transport";
import { EnrollmentRequiredError, InvalidAccessKeyError } from "./auth";
import { UncertainCommandError } from "./ble-remote-api";

type Tab = "live" | "snapshots" | "settings";
type TextSheet =
  | { kind: "save"; head: HeadId; value: string }
  | { kind: "rename"; snapshot: SnapshotSummary; value: string };

const SETTING_GROUPS: Array<{ id: SettingGroup; label: string; description: string }> = [
  { id: "calibration", label: "Calibration", description: "Convert sensor ticks into measured volume." },
  { id: "detection", label: "Shot detection", description: "Choose when a pour begins and ends." },
  { id: "prewet", label: "Prewet", description: "Bounds for the startup phase; excluded from scoring." },
  { id: "extraction", label: "Main extraction", description: "Define the plateau used for comparison." },
  { id: "display", label: "Display", description: "Tune the remote and touchscreen presentation." },
  { id: "developer", label: "Developer", description: "Test-only controller capabilities." },
];

function emptyDevice(): DeviceState {
  const device = createDemoDevice();
  device.deviceName = "No controller";
  device.firmwareVersion = "-";
  device.revision = 0;
  device.lifetimeShots = 0;
  device.batchShots = 0;
  device.snapshots = [];
  device.settings = [];
  device.storageNeedsRepair = false;
  device.capabilities = { ...device.capabilities, demo: false };
  device.heads[1].reference = undefined;
  device.heads[2].reference = undefined;
  return device;
}

function hardwareDuration(milliseconds: number): string {
  return (milliseconds / 1000).toFixed(2);
}

const HEAD_STATUS_LABELS: Record<HeadState["status"], string> = {
  last: "LAST",
  analyzing: "ANALYZING",
  prewet: "PREWET",
  live: "LIVE",
  flush_ignored: "FLUSH IGNORED",
  incomplete: "INCOMPLETE",
  sensor_fault: "SENSOR FAULT",
  storage_error: "STORAGE ERROR",
};

function incompleteReasonLabel(reasons: number): string | undefined {
  if ((reasons & ((1 << 5) | (1 << 6))) === ((1 << 5) | (1 << 6))) return "FLOW DROP";
  const labels: Array<[number, string]> = [
    [1 << 6, "DROP % LOW"], [1 << 5, "DROP RATE LOW"], [1 << 7, "LATE FLOW"],
    [1 << 4, "EARLY FLOW"], [1 << 0, "MAIN VOLUME LOW"], [1 << 1, "MAIN VOLUME HIGH"],
    [1 << 2, "MAIN TIME SHORT"], [1 << 3, "MAIN TIME LONG"], [1 << 8, "PREWET TIMEOUT"],
    [1 << 9, "CYCLE TIMEOUT"], [1 << 12, "RATE SAMPLES"], [1 << 13, "TIMING FAULT"],
    [1 << 10, "BUFFER OVERRUN"], [1 << 14, "COUNTER OVERFLOW"],
  ];
  return labels.find(([flag]) => reasons & flag)?.[1];
}

function hardwareHeadStatus(head: HeadState): string {
  const reason = head.status === "incomplete" ? incompleteReasonLabel(head.reasonFlags) : undefined;
  return reason ? `INCOMPLETE - ${reason}` : HEAD_STATUS_LABELS[head.status];
}

function isRejectedHeadStatus(status: HeadState["status"]): boolean {
  return status === "flush_ignored" || status === "incomplete" || status === "sensor_fault";
}

function statusText(connection: typeof initialRemoteState.connection): string {
  switch (connection) {
    case "connected": return "CONNECTED";
    case "connecting": return "PAIRING";
    case "reconnecting": return "RECONNECTING";
    case "syncing": return "SYNCING";
    case "unsupported": return "UNAVAILABLE";
    case "error": return "ATTENTION";
    default: return "OFFLINE";
  }
}

function commandTone(outcome: CommandOutcome): "success" | "warning" | "error" | "info" {
  if (["ok", "persistence_pending"].includes(outcome.code)) return "success";
  if (["queued", "busy", "confirmation_required"].includes(outcome.code)) return "warning";
  if (["invalid", "not_found", "conflict", "storage_unavailable", "unauthorized"].includes(outcome.code)) return "error";
  return "info";
}

function shouldNotify(outcome: CommandOutcome): boolean {
  return ["busy", "invalid", "not_found", "conflict", "storage_unavailable", "unauthorized"].includes(outcome.code);
}

const COMMAND_PHASE_LABELS = {
  sending: "SENDING",
  applied: "APPLIED",
  queued: "QUEUED",
  saving: "SAVING",
  saved: "SAVED",
  verifying: "VERIFYING",
  error: "FAILED",
} as const;

export function App({ api, mockMode = false }: { api: RemoteApi; mockMode?: boolean }) {
  const [state, dispatch] = useReducer(remoteReducer, initialRemoteState);
  const [tab, setTab] = useState<Tab>("live");
  const [menuOpen, setMenuOpen] = useState(false);
  const [savePickerOpen, setSavePickerOpen] = useState(false);
  const [sheet, setSheet] = useState<TextSheet>();
  const [confirmation, setConfirmation] = useState<ConfirmationRequest>();
  const [settingsOpen, setSettingsOpen] = useState<SettingGroup | undefined>("calibration");
  const [copyState, setCopyState] = useState("COPY URL");
  const [accessEnrollment, setAccessEnrollment] = useState<{
    controllerId: string;
    value: string;
    error?: string;
  }>();
  const support = useMemo(() => mockMode ? { supported: true } : webBluetoothSupport(), [mockMode]);
  const fallbackDevice = useMemo(emptyDevice, []);
  const device = state.device ?? fallbackDevice;
  const connected = state.connection === "connected" && api.connected;
  const lastDeviceId = state.lastDeviceId ?? localStorage.getItem("espress9th.lastDeviceId") ?? undefined;
  const settingsErrors = validateSettings(state.settingsDraft);
  const draftChanged = settingsChanged(state);
  const commandLocked = commandBlocksMutation(state.commandStatus);
  const mutationBusyAction = state.busyAction ?? (commandLocked ? state.commandStatus?.action : undefined);
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const extractionActive = ([1, 2] as const).some((head) => device.heads[head].detectorActive || device.heads[head].demoActive);

  useEffect(() => {
    if (!support.supported) dispatch({ type: "CONNECTION", phase: "unsupported" });
    return api.onEvent((event) => {
      if (event.type === "state") dispatch({ type: "SYNCED", device: event.device, lastDeviceId: api.deviceId });
      else if (event.type === "operation") {
        dispatch({ type: "COMMAND_COMPLETED", requestId: event.requestId, outcome: event.outcome });
        if (event.outcome.confirmation) setConfirmation(event.outcome.confirmation);
        else if (shouldNotify(event.outcome)) notice(event.outcome, `Background operation ${event.outcome.code.replaceAll("_", " ")}.`);
      } else if (event.type === "operation_verifying") {
        dispatch({ type: "COMMAND_COMPLETION_UNCERTAIN", requestId: event.requestId });
        void event.recovery.then(
          () => dispatch({ type: "COMMAND_CLEAR", requestId: event.requestId }),
          (error: unknown) => {
            if (!api.connected) return;
            const message = error instanceof Error
              ? error.message
              : "Controller state verification did not complete.";
            dispatch({ type: "COMMAND_COMPLETION_RECOVERY_FAILED", requestId: event.requestId });
            dispatch({ type: "NOTICE", notice: { id: Date.now(), tone: "error", text: message } });
          },
        );
      } else dispatch({ type: "DISCONNECTED", error: event.reason });
    });
  }, [api, support.supported]);

  useEffect(() => {
    const status = state.commandStatus;
    if (!status || !["applied", "saved", "error"].includes(status.phase)) return;
    const timer = window.setTimeout(
      () => dispatch({ type: "COMMAND_CLEAR", requestId: status.requestId }),
      status.phase === "error" ? 3_000 : 2_000,
    );
    return () => window.clearTimeout(timer);
  }, [state.commandStatus]);

  useEffect(() => {
    const status = state.commandStatus;
    const current = state.device;
    if (!status || !current || status.phase !== "saving") return;
    dispatch({
      type: "COMMAND_SETTLED",
      persistencePending: current.statePersistencePending,
      persistenceFailed: current.statePersistenceFailed,
    });
  }, [
    state.commandStatus?.phase,
    state.device?.statePersistencePending,
    state.device?.statePersistenceFailed,
  ]);

  useEffect(() => {
    if (!sheet && !confirmation && !menuOpen && !savePickerOpen) return;
    const close = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (confirmation) void cancelConfirmation();
      else setSheet(undefined);
      if (!confirmation && !sheet && savePickerOpen) setSavePickerOpen(false);
      if (!confirmation && !sheet && !savePickerOpen) setMenuOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  });

  function notice(outcome: CommandOutcome, fallback: string): void {
    dispatch({
      type: "NOTICE",
      notice: { id: Date.now(), tone: commandTone(outcome), text: outcome.message ?? fallback },
    });
  }

  async function connect(mode: "pair" | "reconnect"): Promise<void> {
    dispatch({ type: "CONNECTION", phase: mode === "pair" ? "connecting" : "reconnecting" });
    try {
      const synced = mode === "pair" ? await api.pair() : await api.reconnect(lastDeviceId);
      if (api.deviceId) localStorage.setItem("espress9th.lastDeviceId", api.deviceId);
      dispatch({ type: "SYNCED", device: synced, lastDeviceId: api.deviceId });
    } catch (error) {
      // The chooser has already granted this device even if a later controller
      // read fails. Retain that identity so the next attempt can use Reconnect
      // instead of making the user choose the same device again.
      if (api.deviceId) {
        localStorage.setItem("espress9th.lastDeviceId", api.deviceId);
        dispatch({ type: "CONNECTION", phase: api.connected ? "connected" : "disconnected", lastDeviceId: api.deviceId });
      }
      if (error instanceof EnrollmentRequiredError || error instanceof InvalidAccessKeyError) {
        setAccessEnrollment({
          controllerId: api.enrollmentControllerId ?? "UNKNOWN",
          value: "",
          error: error instanceof InvalidAccessKeyError ? error.message : undefined,
        });
        dispatch({ type: "CONNECTION", phase: "connecting" });
        return;
      }
      const message = error instanceof DOMException && error.name === "NotFoundError"
        ? "No controller selected. Pairing was cancelled."
        : error instanceof Error ? error.message : "Could not connect to the controller.";
      if (api.connected) {
        dispatch({ type: "CONNECTION", phase: "connected" });
        dispatch({ type: "NOTICE", notice: { id: Date.now(), tone: "error", text: message } });
      } else dispatch({ type: "DISCONNECTED", error: message });
    }
  }

  async function submitAccessEnrollment(): Promise<void> {
    if (!accessEnrollment) return;
    try {
      const synced = await api.enroll(accessEnrollment.value);
      if (api.deviceId) localStorage.setItem("espress9th.lastDeviceId", api.deviceId);
      setAccessEnrollment(undefined);
      dispatch({ type: "SYNCED", device: synced, lastDeviceId: api.deviceId });
    } catch (error) {
      setAccessEnrollment((current) => current ? {
        ...current,
        error: error instanceof Error ? error.message : "Access authorization failed.",
      } : current);
    }
  }

  function cancelAccessEnrollment(): void {
    api.disconnect();
    setAccessEnrollment(undefined);
    dispatch({ type: "CONNECTION", phase: "disconnected" });
  }

  async function run(action: string, execute: () => Promise<CommandOutcome>): Promise<void> {
    dispatch({ type: "BUSY", action });
    dispatch({ type: "COMMAND_START", action });
    try {
      const result = await execute();
      dispatch({ type: "COMMAND_RESULT", action, outcome: result });
      if (result.confirmation) {
        setConfirmation(result.confirmation);
      } else {
        if (shouldNotify(result)) notice(result, `${action} ${result.code.replaceAll("_", " ")}.`);
      }
    } catch (error) {
      if (error instanceof UncertainCommandError) {
        dispatch({ type: "COMMAND_UNCERTAIN", action });
        dispatch({
          type: "NOTICE",
          notice: { id: Date.now(), tone: "warning", text: `${error.message} Verifying the controller state now.` },
        });
        try {
          await error.recovery;
          dispatch({ type: "COMMAND_CLEAR" });
        } catch (recoveryError) {
          const recoveryMessage = recoveryError instanceof Error
            ? recoveryError.message
            : "Controller state verification did not complete.";
          if (!api.connected) dispatch({ type: "DISCONNECTED", error: recoveryMessage });
          else {
            dispatch({ type: "COMMAND_RECOVERY_FAILED", action });
            dispatch({ type: "NOTICE", notice: { id: Date.now(), tone: "error", text: recoveryMessage } });
          }
        }
        return;
      }
      const message = error instanceof Error ? error.message : `${action} failed.`;
      dispatch({ type: "COMMAND_RESULT", action, outcome: { code: "invalid", message } });
      if (api.connected) {
        dispatch({ type: "CONNECTION", phase: "connected" });
        dispatch({ type: "NOTICE", notice: { id: Date.now(), tone: "error", text: message } });
      } else {
        dispatch({ type: "DISCONNECTED", error: message });
      }
    } finally {
      dispatch({ type: "BUSY" });
    }
  }

  async function acceptConfirmation(): Promise<void> {
    if (!confirmation) return;
    const current = confirmation;
    setConfirmation(undefined);
    await run("Confirmation", () => api.confirm(current.token));
  }

  async function cancelConfirmation(): Promise<void> {
    if (!confirmation) return;
    const current = confirmation;
    setConfirmation(undefined);
    try {
      await api.cancel(current.token);
    } catch {
      // Tokens expire safely on-device; a disconnect needs no blind retry.
    }
  }

  function submitSheet(): void {
    if (!sheet) return;
    const value = sheet.value.trim();
    if (!value) return;
    if (sheet.kind === "save") void run("Save snapshot", () => api.saveSnapshot(sheet.head, value));
    else void run("Rename snapshot", () => api.renameSnapshot(sheet.snapshot.id, value));
    setSheet(undefined);
  }

  async function copyUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState("COPIED");
    } catch {
      setCopyState("COPY FAILED");
    }
    window.setTimeout(() => setCopyState("COPY URL"), 1800);
  }

  function navigate(next: Tab): void {
    setTab(next);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  function saveHead(head: HeadId): void {
    setSavePickerOpen(false);
    setSheet({ kind: "save", head, value: `Head ${head} / ${new Date().toLocaleDateString()}` });
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => setMenuOpen(true)} aria-label="Open remote menu" aria-expanded={menuOpen} aria-controls="remote-menu">
          <span className="menu-icon" aria-hidden="true"><i /><i /><i /></span>
          <span>E9 REMOTE<small>ESPRESS9TH</small></span>
        </button>
        {tab === "live" && support.supported ? (
          <FlowActions
            className="landscape-flow-actions"
            device={device}
            connected={connected}
            busyAction={mutationBusyAction}
            onSave={() => setSavePickerOpen(true)}
            onCompare={() => navigate("snapshots")}
            onUnload={() => void run("Unload snapshot", () => api.unloadSnapshot())}
          />
        ) : null}
        <section className={`live-panel live-panel--${state.connection}`} aria-label={`Connection: ${statusText(state.connection)}`}>
          <div className="live-status">
            <span className="status-dot" />
            <span><strong>{connected ? (extractionActive ? "LIVE" : "LAST") : statusText(state.connection)}</strong><small>{connected ? `BLE / ${device.deviceName}` : "CONTROLLER"}</small></span>
          </div>
          {connected ? (
            <button className="connect-action connect-action--quiet" onClick={() => api.disconnect()}>DISCONNECT</button>
          ) : lastDeviceId && support.supported && api.canReconnect ? (
            <div className="connection-actions">
              <button className="connect-action" onClick={() => void connect("reconnect")} disabled={["connecting", "reconnecting"].includes(state.connection)}>RECONNECT</button>
              <button className="connect-action connect-action--quiet" onClick={() => void connect("pair")} disabled={["connecting", "reconnecting"].includes(state.connection)}>PAIR NEW</button>
            </div>
          ) : (
            <button className="connect-action" onClick={() => void connect("pair")} disabled={!support.supported || state.connection === "connecting"}>PAIR</button>
          )}
        </section>
      </header>

      {state.commandStatus ? (
        <section
          className={`command-status command-status--${state.commandStatus.phase}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="command-status-dot" aria-hidden="true" />
          <span>
            <strong>{COMMAND_PHASE_LABELS[state.commandStatus.phase]}</strong>
            <small>{state.commandStatus.action}</small>
          </span>
        </section>
      ) : null}

      {!support.supported ? (
        <section className="unsupported-card" aria-labelledby="unsupported-title">
          <p className="kicker">BLUETOOTH UNAVAILABLE</p>
          <h1 id="unsupported-title">{isIos ? "Open this remote in Bluefy." : "Use a Web Bluetooth browser."}</h1>
          <p>{isIos
            ? "Safari cannot connect to Bluetooth devices. Install Bluefy, then open this exact HTTPS address there. Android Chrome and desktop Chrome or Edge work directly."
            : `${support.reason ?? "This browser cannot reach the controller."} Open the HTTPS page in Chrome or Edge.`}</p>
          <div className="unsupported-actions">
            {isIos ? <a className="button button--primary" href="https://apps.apple.com/us/app/bluefy-web-ble-browser/id1492822055" target="_blank" rel="noreferrer">GET BLUEFY</a> : null}
            <button className="button" onClick={() => void copyUrl()}>{copyState}</button>
          </div>
        </section>
      ) : null}

      <main className={tab === "live" && support.supported ? "main--live" : undefined}>
        {tab === "live" ? (
          <LiveView
            device={device}
            connected={connected}
            busyAction={mutationBusyAction}
            onSave={() => setSavePickerOpen(true)}
            onCompare={() => navigate("snapshots")}
            onUnload={() => void run("Unload snapshot", () => api.unloadSnapshot())}
            onClear={(head) => void run("Clear graph", () => api.clearGraph(head))}
            onDemo={(head) => void run("Run demo", () => api.runDemo(head))}
            onResetBatch={() => void run("Reset batch", () => api.resetBatch())}
          />
        ) : null}
        {tab === "snapshots" ? (
          <SnapshotsView
            device={device}
            connected={connected}
            busyAction={mutationBusyAction}
            onBack={() => navigate("live")}
            onLoad={(id) => void run("Load snapshot", () => api.loadSnapshot(id))}
            onUnload={() => void run("Unload snapshot", () => api.unloadSnapshot())}
            onRename={(snapshot) => setSheet({ kind: "rename", snapshot, value: snapshot.name })}
            onDelete={(id) => void run("Delete snapshot", () => api.deleteSnapshot(id))}
            onRepair={() => void run("Repair storage", () => api.repairStorage())}
          />
        ) : null}
        {tab === "settings" ? (
          <SettingsView
            connected={connected}
            settingsApplyPending={device.settingsApplyPending}
            statePersistencePending={device.statePersistencePending}
            statePersistenceFailed={device.statePersistenceFailed}
            settings={state.settingsDraft.length ? state.settingsDraft : device.settings}
            openGroup={settingsOpen}
            errors={settingsErrors}
            changed={draftChanged}
            busyAction={mutationBusyAction}
            onBack={() => navigate("live")}
            onToggleGroup={(group) => setSettingsOpen(settingsOpen === group ? undefined : group)}
            onChange={(id, value) => dispatch({ type: "DRAFT_SETTING", id, value })}
            onDefaults={() => dispatch({ type: "RESET_DRAFT" })}
            onSave={() => void run("Save settings", () => api.saveSettings(state.settingsDraft))}
          />
        ) : null}
      </main>

      {menuOpen ? (
        <>
          <button className="remote-menu-scrim" aria-label="Close remote menu" onClick={() => setMenuOpen(false)} />
          <aside className="remote-menu" id="remote-menu" aria-label="Remote menu">
            <header><span><strong>E9 REMOTE</strong><small>CONTROLLER MENU</small></span><button aria-label="Close remote menu" onClick={() => setMenuOpen(false)}>X</button></header>
            <nav aria-label="Controller screens">
              {(["live", "snapshots", "settings"] as const).map((item) => (
                <button key={item} className={tab === item ? "active" : ""} aria-current={tab === item ? "page" : undefined} onClick={() => navigate(item)}>
                  <NavIcon kind={item} />
                  <span><strong>{item === "live" ? "DASHBOARD" : item === "snapshots" ? "COMPARISON SNAPSHOTS" : "DETECTOR SETTINGS"}</strong><small>{item === "live" ? "Flow graph and shot metrics" : item === "snapshots" ? "Load, rename, or delete references" : "Detection and display configuration"}</small></span>
                </button>
              ))}
            </nav>
            <section className="menu-actions">
              <p className="kicker">REMOTE ACTIONS</p>
              <div><button disabled={!connected || !device.capabilities.demo || device.heads[1].detectorActive || Boolean(mutationBusyAction)} onClick={() => { setMenuOpen(false); void run("Run demo", () => api.runDemo(1)); }}>RUN H1 DEMO</button><button disabled={!connected || !device.capabilities.demo || device.heads[2].detectorActive || Boolean(mutationBusyAction)} onClick={() => { setMenuOpen(false); void run("Run demo", () => api.runDemo(2)); }}>RUN H2 DEMO</button></div>
              <button disabled={!connected || Boolean(mutationBusyAction)} onClick={() => { setMenuOpen(false); void run("Reset batch", () => api.resetBatch()); }}>RESET BATCH COUNTER</button>
            </section>
            <footer><span className={`status-dot ${connected ? "status-dot--connected" : ""}`} /><span><strong>{connected ? "BLE CONNECTED" : "BLE OFFLINE"}</strong><small>{connected ? device.deviceName : "Pair from the top-right panel"}</small></span></footer>
          </aside>
        </>
      ) : null}

      <div className="notice-stack" aria-live="polite">
        {state.notices.map((item) => (
          <button key={item.id} className={`notice notice--${item.tone}`} onClick={() => dispatch({ type: "DISMISS_NOTICE", id: item.id })}>{item.text}<span aria-hidden="true">x</span></button>
        ))}
      </div>

      {savePickerOpen ? (
        <dialog open className="dialog" aria-labelledby="save-head-title" onCancel={(event) => { event.preventDefault(); setSavePickerOpen(false); }}>
          <form method="dialog" onSubmit={(event) => event.preventDefault()}>
            <p className="kicker">SAVE SNAPSHOT</p>
            <h2 id="save-head-title">Save which head?</h2>
            <div className="head-picker"><button className="button button--h1" disabled={!connected || !device.heads[1].saveAvailable} onClick={() => saveHead(1)}>H1</button><button className="button button--h2" disabled={!connected || !device.heads[2].saveAvailable} onClick={() => saveHead(2)}>H2</button><button className="button" onClick={() => setSavePickerOpen(false)}>CANCEL</button></div>
          </form>
        </dialog>
      ) : null}

      {sheet ? (
        <dialog open className="dialog" aria-labelledby="text-dialog-title" onCancel={(event) => { event.preventDefault(); setSheet(undefined); }}>
          <form method="dialog" onSubmit={(event) => { event.preventDefault(); submitSheet(); }}>
            <p className="kicker">{sheet.kind === "save" ? `HEAD ${sheet.head}` : "SNAPSHOT"}</p>
            <h2 id="text-dialog-title">{sheet.kind === "save" ? "Save this shot" : "Rename snapshot"}</h2>
            <label className="text-field">Name<input autoFocus maxLength={24} value={sheet.value} onChange={(event) => setSheet({ ...sheet, value: event.target.value })} /></label>
            <small>{new TextEncoder().encode(sheet.value).length} / 24 UTF-8 bytes</small>
            <div className="dialog-actions"><button type="button" className="button" onClick={() => setSheet(undefined)}>CANCEL</button><button className="button button--primary" disabled={!sheet.value.trim() || new TextEncoder().encode(sheet.value).length > 24}>SAVE</button></div>
          </form>
        </dialog>
      ) : null}

      {confirmation ? (
        <dialog open className="dialog dialog--danger" aria-labelledby="confirm-title" onCancel={(event) => { event.preventDefault(); void cancelConfirmation(); }}>
          <form method="dialog" onSubmit={(event) => { event.preventDefault(); void acceptConfirmation(); }}>
            <p className="kicker">CONFIRM ONCE</p>
            <h2 id="confirm-title">{confirmation.title}</h2>
            <p>{confirmation.detail}</p>
            <small>This device-issued confirmation expires in 30 seconds.</small>
            <div className="dialog-actions"><button type="button" className="button" onClick={() => void cancelConfirmation()}>CANCEL</button><button className="button button--danger">CONFIRM</button></div>
          </form>
        </dialog>
      ) : null}

      {accessEnrollment ? (
        <dialog open className="dialog" aria-labelledby="access-dialog-title" onCancel={(event) => { event.preventDefault(); cancelAccessEnrollment(); }}>
          <form method="dialog" onSubmit={(event) => { event.preventDefault(); void submitAccessEnrollment(); }}>
            <span className="eyebrow">CONTROLLER AUTHORIZATION</span>
            <h2 id="access-dialog-title">Enter access key</h2>
            <p>On the controller, tap the Bluetooth icon. Enter the grouped key shown beside the QR code.</p>
            <label className="access-field">ACCESS KEY
              <input
                autoFocus
                autoComplete="off"
                inputMode="text"
                spellCheck={false}
                placeholder="ABCDE-FGHIJ-KLMNO-PQRST-UVWXY-Z"
                value={accessEnrollment.value}
                onChange={(event) => setAccessEnrollment({ ...accessEnrollment, value: event.target.value.toUpperCase(), error: undefined })}
              />
            </label>
            <small>CONTROLLER {accessEnrollment.controllerId}</small>
            {accessEnrollment.error ? <p className="field-error">{accessEnrollment.error}</p> : null}
            <div className="dialog-actions"><button type="button" className="button" onClick={cancelAccessEnrollment}>CANCEL</button><button className="button button--primary" disabled={!accessEnrollment.value.trim()}>AUTHORIZE</button></div>
          </form>
        </dialog>
      ) : null}
    </div>
  );
}

function FlowActions({
  className, device, connected, busyAction, showTitle = false, onSave, onCompare, onUnload,
}: {
  className: string;
  device: DeviceState;
  connected: boolean;
  busyAction?: string;
  showTitle?: boolean;
  onSave: () => void;
  onCompare: () => void;
  onUnload: () => void;
}) {
  const activeReference = device.snapshots.find((snapshot) => snapshot.active);
  const canSave = connected && ([1, 2] as const).some((head) => device.heads[head].saveAvailable) && !busyAction;
  const savePending = device.heads[1].pendingSave || device.heads[2].pendingSave;
  return (
    <div className={`flow-actions ${className}`} role="toolbar" aria-label="Snapshot controls">
      {showTitle ? <span className="hardware-title">E9 FLOW</span> : null}
      <button className="toolbar-action toolbar-action--primary" disabled={!canSave} onClick={onSave}>{savePending ? "ARMED" : "SAVE"}</button>
      <button className={activeReference ? "toolbar-reference toolbar-reference--active" : "toolbar-reference"} onClick={onCompare}>{activeReference?.name ?? "COMPARE: NONE"}</button>
      <button className="toolbar-action" disabled={!connected || !activeReference || Boolean(busyAction)} onClick={onUnload}>UNLOAD</button>
    </div>
  );
}

function LiveView({
  device, connected, busyAction, onSave, onCompare, onUnload, onClear, onDemo, onResetBatch,
}: {
  device: DeviceState;
  connected: boolean;
  busyAction?: string;
  onSave: () => void;
  onCompare: () => void;
  onUnload: () => void;
  onClear: (head: HeadId) => void;
  onDemo: (head: HeadId) => void;
  onResetBatch: () => void;
}) {
  const showPlateauMarkers = Boolean(device.settings.find((setting) => setting.key === "showPlateauMarkers")?.value);
  const displayedFrames = useFrameInterpolation(device.heads);
  return (
    <section className="view live-view" aria-labelledby="live-title">
      <h1 className="sr-only" id="live-title">Flow dashboard</h1>
      <FlowActions className="hardware-toolbar" device={device} connected={connected} busyAction={busyAction} showTitle onSave={onSave} onCompare={onCompare} onUnload={onUnload} />
      <section className="graph-panel hardware-graph">
        <header><span className={!connected || isRejectedHeadStatus(device.heads[1].status) || device.heads[1].status === "storage_error" ? "head-status head-status--error" : "head-status head-status--h1"}>H1 {connected ? hardwareHeadStatus(device.heads[1]) : "OFFLINE"}</span><span>FLOW RATE</span><span className={!connected || isRejectedHeadStatus(device.heads[2].status) || device.heads[2].status === "storage_error" ? "head-status head-status--error" : "head-status head-status--h2"}>H2 {connected ? hardwareHeadStatus(device.heads[2]) : "OFFLINE"}</span></header>
        <ShotGraph heads={device.heads} showPlateauMarkers={showPlateauMarkers} displayedFrames={displayedFrames} />
        <footer><span>SHOT TIME / 250 MS</span><small>{connected ? `REV ${device.revision}` : "CONNECTION LOST / LAST DATA"}</small></footer>
      </section>
      <div className="metric-rack">
        {([1, 2] as const).map((id) => <HeadMetricPanel key={id} id={id} head={device.heads[id]} displayed={displayedFrames[id]} connected={connected} busy={Boolean(busyAction)} demoEnabled={device.capabilities.demo} onClear={() => onClear(id)} onDemo={() => onDemo(id)} />)}
        <article className="metric-panel metric-panel--match">
          <header><span className="metric-icon metric-icon--match" aria-hidden="true" />MATCH</header>
          <div className="metric-row"><span className="metric-row-label metric-row-label--h1">H1</span><strong>{device.heads[1].score === undefined ? "-" : `${Math.round(device.heads[1].score)}%`}</strong></div>
          <div className="metric-row"><span className="metric-row-label metric-row-label--h2">H2</span><strong>{device.heads[2].score === undefined ? "-" : `${Math.round(device.heads[2].score)}%`}</strong></div>
        </article>
        <article className="metric-panel metric-panel--shots">
          <header><span className="metric-icon metric-icon--shots" aria-hidden="true" />SHOTS</header>
          <div className="counter-row"><span>LIFETIME</span><strong>{device.lifetimeShots}</strong></div>
          <div className="counter-row"><span>BATCH <small>HOLD RESET</small></span><strong>{device.batchShots}</strong></div>
          <HoldAction className="metric-gesture" label="Hold to reset the batch shot counter" disabled={!connected || Boolean(busyAction)} onHold={onResetBatch} />
        </article>
      </div>
    </section>
  );
}

function HeadMetricPanel({ id, head, displayed, connected, busy, demoEnabled, onClear, onDemo }: { id: HeadId; head: HeadState; displayed: { volumeMl: number; elapsedMs: number }; connected: boolean; busy: boolean; demoEnabled: boolean; onClear: () => void; onDemo: () => void }) {
  const active = head.detectorActive || head.demoActive;
  const canClear = connected && !active && head.trace.points.length > 0 && !busy;
  const canDemo = connected && !active && demoEnabled && !busy;
  return (
    <article className={`metric-panel metric-panel--h${id}`}>
      <header><span className={`metric-icon metric-icon--head metric-icon--h${id}`} aria-hidden="true" />HEAD {id}<small>{connected ? (head.pendingSave ? "ARMED" : hardwareHeadStatus(head)) : "OFFLINE"}</small></header>
      <div className="metric-row"><strong>{displayed.volumeMl.toFixed(2)}</strong><span>mL</span></div>
      <div className="metric-row"><strong>{hardwareDuration(displayed.elapsedMs)}</strong><span>sec</span></div>
      <HoldAction className="metric-gesture" label={`Head ${id}: tap to clear the graph${canDemo ? ", hold to run a demo" : ""}`} disabled={!canClear && !canDemo} onPress={canClear ? onClear : undefined} onHold={canDemo ? onDemo : undefined} />
    </article>
  );
}

function HoldAction({ className, label, disabled, onPress, onHold }: { className?: string; label: string; disabled: boolean; onPress?: () => void; onHold?: () => void }) {
  const timer = useRef<number | undefined>(undefined);
  const held = useRef(false);
  const cancelTimer = (): void => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  };
  return <button type="button" className={className} aria-label={label} disabled={disabled} onPointerDown={() => {
    held.current = false;
    cancelTimer();
    if (onHold) timer.current = window.setTimeout(() => { held.current = true; onHold(); }, 700);
  }} onPointerUp={() => {
    cancelTimer();
    if (!held.current) onPress?.();
    held.current = false;
  }} onPointerCancel={cancelTimer} onPointerLeave={cancelTimer} onContextMenu={(event) => event.preventDefault()} onClick={(event) => { if (event.detail === 0) onPress?.(); }}><span className="sr-only">{label}</span></button>;
}

function SnapshotsView({ device, connected, busyAction, onBack, onLoad, onUnload, onRename, onDelete, onRepair }: {
  device: DeviceState;
  connected: boolean;
  busyAction?: string;
  onBack: () => void;
  onLoad: (id: number) => void;
  onUnload: () => void;
  onRename: (snapshot: SnapshotSummary) => void;
  onDelete: (id: number) => void;
  onRepair: () => void;
}) {
  const active = device.snapshots.find((snapshot) => snapshot.active);
  return (
    <section className="view" aria-labelledby="snapshots-title">
      <header className="secondary-toolbar">
        <button className="button" onClick={onBack}>BACK</button>
        <div><p className="kicker">DEVICE STORAGE</p><h1 id="snapshots-title">Comparison snapshots</h1></div>
        <div className="capacity"><strong>{device.snapshots.length}</strong><span>/ {device.capabilities.maxSnapshots}</span><small>SLOTS USED</small></div>
      </header>
      {active ? <div className="active-reference"><span><i className="swatch swatch--reference" />ACTIVE REFERENCE</span><strong>{active.name}</strong><button disabled={!connected || Boolean(busyAction)} onClick={onUnload}>UNLOAD</button></div> : null}
      <div className="snapshot-list">
        {device.snapshots.length ? device.snapshots.map((snapshot) => (
          <article key={snapshot.id} className={snapshot.active ? "snapshot-row snapshot-row--active" : "snapshot-row"}>
            <div className="snapshot-index">{String(snapshot.id).slice(-2).padStart(2, "0")}</div>
            <div className="snapshot-copy"><strong>{snapshot.name}</strong><span>SHOT #{snapshot.capturedShotNumber} / {snapshot.classification}</span></div>
            <dl><div><dt>VOLUME</dt><dd>{snapshot.volumeMl.toFixed(2)} mL</dd></div><div><dt>TIME</dt><dd>{hardwareDuration(snapshot.durationMs)} sec</dd></div><div><dt>SAMPLES</dt><dd>{snapshot.sampleCount}</dd></div></dl>
            <div className="snapshot-actions">
              <button className="button button--primary" disabled={!connected || snapshot.active || Boolean(busyAction)} onClick={() => onLoad(snapshot.id)}>{snapshot.active ? "LOADED" : "LOAD"}</button>
              <button className="icon-button" aria-label={`Rename ${snapshot.name}`} disabled={!connected || Boolean(busyAction)} onClick={() => onRename(snapshot)}>RENAME</button>
              <button className="icon-button icon-button--danger" aria-label={`Delete ${snapshot.name}`} disabled={!connected || Boolean(busyAction)} onClick={() => onDelete(snapshot.id)}>DELETE</button>
            </div>
          </article>
        )) : <div className="empty-state"><strong>NO SNAPSHOTS</strong><p>Complete a shot, then use Save from the Live view.</p></div>}
      </div>
      <footer className="snapshot-footer"><span>{device.storageNeedsRepair ? "SNAPSHOT STORAGE NEEDS REPAIR" : "SNAPSHOT STORAGE OK"}</span>{device.storageNeedsRepair ? <button className="button button--danger" disabled={!connected || !device.capabilities.repairStorage || Boolean(busyAction)} onClick={onRepair}>REPAIR</button> : null}</footer>
    </section>
  );
}

function SettingsView({
  connected, settingsApplyPending, statePersistencePending, statePersistenceFailed, settings, openGroup, errors, changed, busyAction, onBack, onToggleGroup, onChange, onDefaults, onSave,
}: {
  connected: boolean;
  settingsApplyPending: boolean;
  statePersistencePending: boolean;
  statePersistenceFailed: boolean;
  settings: SettingDescriptor[];
  openGroup?: SettingGroup;
  errors: Map<number, string>;
  changed: boolean;
  busyAction?: string;
  onBack: () => void;
  onToggleGroup: (group: SettingGroup) => void;
  onChange: (id: number, value: number | boolean) => void;
  onDefaults: () => void;
  onSave: () => void;
}) {
  return (
    <section className="view settings-view" aria-labelledby="settings-title">
      <header className="secondary-toolbar"><button className="button" onClick={onBack}>BACK</button><div><p className="kicker">CONTROLLER CONFIGURATION</p><h1 id="settings-title">Detector settings</h1></div></header>
      <div className="settings-groups">
        {!settings.length ? <div className="empty-state"><strong>PAIR TO LOAD SETTINGS</strong><p>Configuration is read from the controller after every connection.</p></div> : null}
        {SETTING_GROUPS.map((group) => {
          const items = settings.filter((setting) => setting.group === group.id);
          if (!items.length) return null;
          const expanded = openGroup === group.id;
          return (
            <section key={group.id} className="settings-group">
              <button className="settings-group-toggle" aria-expanded={expanded} onClick={() => onToggleGroup(group.id)}><span><strong>{group.label}</strong><small>{group.description}</small></span><span aria-hidden="true">{expanded ? "-" : "+"}</span></button>
              {expanded ? <div className="setting-fields">{items.map((setting) => (
                <SettingField key={setting.id} setting={setting} error={errors.get(setting.id)} disabled={!connected || Boolean(busyAction)} onChange={(value) => onChange(setting.id, value)} />
              ))}</div> : null}
            </section>
          );
        })}
      </div>
      <div className="settings-savebar"><button className="button" disabled={!connected || Boolean(busyAction)} onClick={onDefaults}>RESTORE DEFAULTS</button><span>{errors.size ? `${errors.size} INVALID ${errors.size === 1 ? "FIELD" : "FIELDS"}` : changed ? "UNSAVED CHANGES" : settingsApplyPending ? "APPLYING AFTER BOTH HEADS" : statePersistenceFailed ? "CONTROLLER STORAGE FAILED" : statePersistencePending ? "SAVING TO CONTROLLER" : "UP TO DATE"}</span><button className="button button--primary" disabled={!connected || !changed || errors.size > 0 || Boolean(busyAction)} onClick={onSave}>SAVE SETTINGS</button></div>
    </section>
  );
}

function SettingField({ setting, error, disabled, onChange }: { setting: SettingDescriptor; error?: string; disabled: boolean; onChange: (value: number | boolean) => void }) {
  if (setting.kind === "boolean") {
    return <label className="setting-field setting-field--toggle"><span><strong>{setting.label}</strong><small>{setting.help}</small></span><input type="checkbox" role="switch" checked={Boolean(setting.value)} disabled={disabled} onChange={(event) => onChange(event.target.checked)} /><i aria-hidden="true" /></label>;
  }
  return (
    <label className={error ? "setting-field setting-field--error" : "setting-field"}>
      <span><strong>{setting.label}</strong><small>{setting.help}</small></span>
      <span className="number-input"><input type="number" value={Number(setting.value)} min={setting.min} max={setting.max} step={setting.step} disabled={disabled} aria-invalid={Boolean(error)} onChange={(event) => onChange(event.target.valueAsNumber)} /><em>{setting.unit}</em></span>
      {error ? <small className="field-error">{error}</small> : null}
    </label>
  );
}

function NavIcon({ kind }: { kind: Tab }) {
  if (kind === "live") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 14h3l2-7 4 12 3-9 2 4h4" /></svg>;
  if (kind === "snapshots") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h14v16H5zM8 8h8M8 12h5" /></svg>;
  return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></svg>;
}
